import { z } from "zod";
import { conversationModeValues, handoffReasonCodeValues, handoffStatusValues, handoffTargetKindValues, } from "./conversation-handoff.js";
import { memoryFormSchema, memoryKindSchema, memoryProvenanceSchema } from "./customer-memory.js";
import { customerChannelIdSchema } from "./messaging.js";
import { memoryConfirmationLevelValues, staffCustomerRelationshipValues, } from "./staff-customer-memory.js";
// The staff conversation context (CHT-732; docs/architecture/milestone-c-contracts.md sections 4.6 and 10.3; ADR 0012).
// `GET /staff/conversations/:conversationId/context` (MC-E1) answers ONE call for the side pane of the staff workspace. It
// composes what staff may already read (the handoff with its history, the open requests and payments, the current trip, a
// summary of the memory) and nothing else: no message text, no email, no phone number, no other customer.
//
// Rules, same as `conversation-handoff.ts`: strict top-level shapes, ISO 8601 text for times, codes repeated from the
// domain. A section the caller's role may not read is `null` (today every role that may open the workspace may read every
// section; the shape keeps the rule visible). The staff-internal texts (`reasonSummary`, `internalReason`, `summary.text`)
// never reach a customer view, an audit row, a log or analytics.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const uuid = z.string().regex(UUID);
const instant = z.iso.datetime({ offset: false });
const version = z.number().int().min(1).max(2_147_483_647);
/** A staff member as the workspace names them: the id and the name staff see (never an email). */
export const staffContextStaffRefSchema = z.strictObject({
    staffUserId: uuid,
    displayName: z.string().min(1).max(120),
});
/** How the caller may see this conversation (contract 10.3), strongest first. */
export const staffContextRelationshipValues = [
    "SUPERVISOR",
    "HANDOFF_OWNER",
    "HANDOFF_TARGET",
    ...staffCustomerRelationshipValues.filter((value) => value !== "SUPERVISOR"),
];
export const staffContextHandoffEventSchema = z.strictObject({
    version,
    eventType: z.enum([
        "REQUESTED",
        "ROUTED",
        "REASSIGNED",
        "ACCEPTED",
        "OWNER_TRANSFERRED",
        "RELEASED",
        "CANCELLED",
    ]),
    statusAfter: z.enum(handoffStatusValues),
    actorKind: z.enum(["customer", "staff", "system"]),
    target: staffContextStaffRefSchema.nullable(),
    owner: staffContextStaffRefSchema.nullable(),
    /** Staff-internal. */
    internalReason: z.string().max(500).nullable(),
    occurredAt: instant,
});
export const staffContextHandoffSchema = z.strictObject({
    id: uuid,
    status: z.enum(handoffStatusValues),
    /** The version the commands carry as `expectedVersion`. */
    version,
    reasonCode: z.enum(handoffReasonCodeValues),
    /** Staff-internal summary of why the handoff was requested. */
    reasonSummary: z.string().max(2_000).nullable(),
    targetKind: z.enum(handoffTargetKindValues).nullable(),
    target: staffContextStaffRefSchema.nullable(),
    owner: staffContextStaffRefSchema.nullable(),
    requestedAt: instant,
    routedAt: instant.nullable(),
    acceptedAt: instant.nullable(),
    /** Oldest first, at most 20 (the newest are kept). */
    events: z.array(staffContextHandoffEventSchema).max(20),
});
/** The latest handoff summary snapshot (lane C, CHT-734). `null` until a snapshot exists. Staff-internal text. */
export const staffContextSummarySchema = z.strictObject({
    version,
    generatedBy: z.enum(["SYSTEM", "COPILOT", "STAFF"]),
    text: z.string().max(2_000),
    createdAt: instant,
});
export const staffContextRequestSchema = z.strictObject({
    id: uuid,
    requestType: z.string().min(1).max(64),
    state: z.string().min(1).max(32),
    title: z.string().max(200),
    updatedAt: instant,
});
export const staffContextPaymentSchema = z.strictObject({
    id: uuid,
    status: z.string().min(1).max(32),
    amountMinor: z.number().int().min(0),
    currency: z.string().regex(/^[A-Z]{3}$/u),
    description: z.string().max(300),
    statusChangedAt: instant,
});
export const staffContextTripSchema = z.strictObject({
    id: uuid,
    status: z.enum(["PLANNING", "UPCOMING", "ACTIVE", "COMPLETED", "CANCELLED"]),
    title: z.string().max(200),
    startsOn: z.string().max(10).nullable(),
    endsOn: z.string().max(10).nullable(),
    startTimeZone: z.string().max(64).nullable(),
    endTimeZone: z.string().max(64).nullable(),
});
/** A short, non-sensitive memory line. `labelKey` names a vocabulary code; `value` is the text of a TEXT value. */
export const staffContextMemoryHighlightSchema = z.strictObject({
    factId: uuid,
    kind: memoryKindSchema,
    form: memoryFormSchema.nullable(),
    value: z.string().max(200).nullable(),
    labelKey: z.string().min(1).max(96).nullable(),
    provenance: memoryProvenanceSchema,
    confirmationLevel: z.enum(memoryConfirmationLevelValues),
});
export const staffContextMemorySchema = z.strictObject({
    activeCount: z.number().int().min(0),
    reviewCount: z.number().int().min(0),
    disputedCount: z.number().int().min(0),
    /** The list was cut: more memories exist than were counted. */
    truncated: z.boolean(),
    /** Up to 6 ACTIVE facts that are not `SENSITIVE`. The memory page holds the rest. */
    highlights: z.array(staffContextMemoryHighlightSchema).max(6),
});
export const staffConversationContextSchema = z.strictObject({
    conversationId: uuid,
    mode: z.enum(conversationModeValues),
    modeChangedAt: instant,
    /** `null` until the customer opened the chat once: there is no thread to read yet. */
    channel: z.strictObject({ type: z.literal("messaging"), id: customerChannelIdSchema }).nullable(),
    customer: z.strictObject({ id: uuid, displayName: z.string().min(1).max(120) }),
    viewer: z.strictObject({
        staffUserId: uuid,
        isSupervisor: z.boolean(),
        relationship: z.enum(staffContextRelationshipValues),
    }),
    handoff: staffContextHandoffSchema.nullable(),
    summary: staffContextSummarySchema.nullable(),
    /** Colleagues the caller may hand the conversation to: ACTIVE staff with an effective assignment to the customer. */
    colleagues: z.array(staffContextStaffRefSchema).max(20),
    openWork: z
        .strictObject({
        requests: z.array(staffContextRequestSchema).max(10),
        payments: z.array(staffContextPaymentSchema).max(10),
    })
        .nullable(),
    trip: staffContextTripSchema.nullable(),
    memory: staffContextMemorySchema.nullable(),
});
/** `POST /staff/conversations/:conversationId/join` (MC-E2): the caller is now a member of the customer channel. */
export const staffConversationJoinResultSchema = z.strictObject({
    channel: z.strictObject({ type: z.literal("messaging"), id: customerChannelIdSchema }),
});
